import type { MutableRefObject} from "react";
import React, { useMemo, useState } from "react";
import {
  FormLayout,
  LegacyStack,
  TextField,
  Text,
  Checkbox,
  Label,
  InlineGrid,
  Select,
  BlockStack,
} from "@shopify/polaris";
import { updateHorseInventoryLevels, type HorseInventoryLevel } from "../../api_utils/requests";
import { assertNumber, assertString, extractMessageFromError, isEmpty, softAssertNumber } from "../../helper_functions/utils";
import type { Option } from "../../api_utils/types";

const defaultReason = "correction";
const defaultComment = "";

const options = [
  { label: "Correction (default)", value: "correction", key: "correction" },
  { label: "Count", value: "count_inventory", key: "count_inventory" },
  { label: "Received", value: "received", key: "received" },
  { label: "Return restock", value: "return_restock", key: "return_restock" },
  { label: "Damaged", value: "damaged", key: "damaged" },
  { label: "Theft or loss", value: "theft_or_loss", key: "theft_or_loss" },
  { label: "Promotion or donation", value: "promotion_or_donation", key: "promotion_or_donation" },
  { label: "Other", value: "other", key: "other" },
];

export function QuantityChangeForm({
  horseInventoryLevels,
  horseVariantId,
  disabled,
  tracked,
  saveCallback,
  discardCallback,
  setTotalAvailableQuantity,
  setErrorMessage,
  setIsDirty
}: {
  readonly horseInventoryLevels: HorseInventoryLevel[];
  readonly horseVariantId: number;
  readonly disabled: boolean;
  readonly tracked: boolean;
  readonly saveCallback: MutableRefObject<() => Promise<HorseInventoryLevel[]>>;
  readonly discardCallback: MutableRefObject<() => void>;
  readonly setTotalAvailableQuantity?: (totalAvailableQuantity: string) => void;
  readonly setErrorMessage: (message: string) => void;
  readonly setIsDirty: (isDirty: boolean) => void;
}): React.JSX.Element {
  const determineIsDirty = (newChangedQuantities: Record<string, string>, newTrackedQuantity: boolean, newSelectedReason: string, newStockUpdateComment: string): boolean => {
    const isDirty = !isEmpty(newChangedQuantities) || newTrackedQuantity !== tracked || newSelectedReason !== defaultReason || newStockUpdateComment !== defaultComment;
    setIsDirty(isDirty);
    return isDirty;
  }

  const [trackedQuantity, setTrackedQuantity] = useState(tracked);
  const handleSetTrackedQuantity = (newTrackedQuantity: boolean): void => {
    setTrackedQuantity(newTrackedQuantity);
    determineIsDirty(
      changedQuantities,
      newTrackedQuantity,
      selectedReason,
      stockUpdateComment
    )
  };
  const [selectedReason, setSelectedReason] = useState(defaultReason);
  const handleSetSelectedReason = (newSelectedReason: string): void => {
    setSelectedReason(newSelectedReason);
    determineIsDirty(
      changedQuantities,
      trackedQuantity,
      newSelectedReason,
      stockUpdateComment
    )
  };
  const [stockUpdateComment, setStockUpdateComment] = useState(defaultComment);
  const handleSetStockUpdateComment = (newStockUpdateComment: string): void => {
    setStockUpdateComment(newStockUpdateComment);
    determineIsDirty(
      changedQuantities,
      trackedQuantity,
      selectedReason,
      newStockUpdateComment
    )
  };
  const [changedQuantities, setChangedQuantities] = useState<Record<string, string>>({});

  const horseInventoryLevelsIndex = useMemo<Record<string, HorseInventoryLevel>>(() => {
    return horseInventoryLevels.reduce((acc, hil) => {
      acc[hil.updateOrCreateId] = hil;
      return acc;
    }, {});
  }, [horseInventoryLevels]);

  const handleChange = (newAvailable: string, updateOrCreateId: string): void => {
    const cleanAvailable = newAvailable?.replace(/[^\d.-]/g, "");

    const newChangedQuantities = { ...changedQuantities };
    if (cleanAvailable === horseInventoryLevelsIndex[updateOrCreateId]?.available?.toString()) {
      delete newChangedQuantities[updateOrCreateId];
    } else {
      newChangedQuantities[updateOrCreateId] = cleanAvailable;
    }
    setChangedQuantities(newChangedQuantities);
    determineIsDirty(
      newChangedQuantities,
      trackedQuantity,
      selectedReason,
      stockUpdateComment
    );
  };

  const handleSave = async (): Promise<HorseInventoryLevel[]> => {
    // An untracked variant holds no quantities, so don't send the ones still in the form
    const horse_inventory_levels = trackedQuantity ? horseInventoryLevels.filter((hil) => {
      return changedQuantities[hil.updateOrCreateId] !== undefined;
    }).map((hil) => ({
      id: hil.id,
      // If it already exists, allow it to be changed to 0
      // but if it doesn't exist, then empty still means untracked
      available: hil.id
        ? assertNumber(changedQuantities[hil.updateOrCreateId])
        : softAssertNumber(changedQuantities[hil.updateOrCreateId]),
      horse_location_id: hil.horse_location_id,
      horse_variant_id: hil.horse_variant_id,
    })) : [];

    const payload = {
      horse_variant_id: horseVariantId,
      horse_inventory_levels,
      tracked: trackedQuantity,
      reason: selectedReason,
      comment: stockUpdateComment,
    };

    try{
      const updatedHorseInventoryLevels = await updateHorseInventoryLevels(payload)
      setChangedQuantities({});
      setStockUpdateComment(defaultComment);
      setSelectedReason(defaultReason);
      setIsDirty(false);
      if (setTotalAvailableQuantity && trackedQuantity) {
        setTotalAvailableQuantity(updatedHorseInventoryLevels.reduce((accum, hil) => accum + hil.available, 0).toString());
      } else if (setTotalAvailableQuantity) {
        setTotalAvailableQuantity("∞");
      }

      return updatedHorseInventoryLevels;
    } catch (err: unknown) {
      Rollbar.error(err, payload);
      const errorMessage = extractMessageFromError(err);
      setErrorMessage(errorMessage);
      throw err;
    }
  };
  saveCallback.current = handleSave;

  const handleDiscard = (): void => {
    setChangedQuantities({});
    setTrackedQuantity(tracked);
    setSelectedReason(defaultReason);
    setStockUpdateComment(defaultComment);
    determineIsDirty(
      {},
      tracked,
      defaultReason,
      defaultComment
    );
  };
  discardCallback.current = handleDiscard;

  const rows = horseInventoryLevels.map((hil) => {
    const available = trackedQuantity
      ? changedQuantities[hil.updateOrCreateId] !== undefined
        ? changedQuantities[hil.updateOrCreateId]
        : hil.available !== undefined
          ? assertString(hil.available)
          : ""
      : "";

    return (
      <InlineGrid columns={2} key={`row-${hil.updateOrCreateId}`}>
        <Label id={`${hil.horse_location_id}-label`} key={`${hil.horse_location_id}-label`}>
          {hil.horse_location.name}
        </Label>
        <TextField
          autoComplete="off"
          disabled={disabled || !trackedQuantity}
          inputMode="numeric"
          key={`field-${hil.updateOrCreateId}`}
          label={hil.horse_location.name}
          labelHidden
          onChange={(newAvailable: string): void => {
            handleChange(newAvailable, hil.updateOrCreateId);
          }}
          selectTextOnFocus
          type="integer"
          value={available}
        />
      </InlineGrid>
    );
  });

  return (
    <FormLayout key={`horse-variants_hil-${horseVariantId}-quantity-change-form`}>
      {horseInventoryLevels.length > 0 && (
        <FormLayout.Group>
          <LegacyStack alignment="baseline" distribution="fillEvenly">
            <div style={{ display: "flex", alignItems: "center" }}>
              <Checkbox checked={trackedQuantity} disabled={disabled} label="Track quantity" onChange={handleSetTrackedQuantity} />
            </div>
          </LegacyStack>
        </FormLayout.Group>
      )}
      {horseInventoryLevels.length === 0 ? (
        <Text as="p" variant="bodySm">
          No inventory levels found
        </Text>
      ) : (
        <BlockStack gap="200">
          {rows}
          <Select
            disabled={disabled}
            label="Reason"
            onChange={handleSetSelectedReason}
            options={options satisfies Option[]}
            value={selectedReason}
          />
          <InlineGrid>
            <Label id="stock-comment">Comment</Label>
            <TextField
              autoComplete="off"
              disabled={disabled}
              inputMode="text"
              label="test"
              labelHidden
              multiline={2}
              onChange={handleSetStockUpdateComment}
              selectTextOnFocus
              type="text"
              value={stockUpdateComment}
            />
          </InlineGrid>
        </BlockStack>
      )}
    </FormLayout>
  );
}
